import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  IUserRolePermission,
  IUserRolePermissionProps,
} from "./UserRole.model";
import { userRolePermissionService } from "../../service/userRolePermission/userRolePermission.service";
import { userRoleService } from "../../service/UserRole/userRole.service";
import { MessageContext } from "../../context/MessageContext";
import { useAuthContext } from "../../context/AuthContext";

function UserRolePermission({
  selectedRole,
  loadUserRoles,
}: IUserRolePermissionProps) {
  const [loading, setLoading] = useState(false);
  const { pushMessageForToast } = useContext(MessageContext);
  const { currentUser, refreshCurrentUser } = useAuthContext();

  const [data, setData] = useState<IUserRolePermission[]>([]);
  const groupedPermissions = useMemo(() => groupByModel(data), [data]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>(
    [],
  );
  useEffect(() => {
    loadUserRolesPermission();
  }, []);

  useEffect(() => {
    if (
      selectedRole &&
      selectedRole?.permissions &&
      selectedRole?.permissions?.length > 0
    ) {
      setSelectedPermissionIds(selectedRole.permissions.map((p) => p.id));
    }
  }, [selectedRole]);

  const loadUserRolesPermission = async () => {
    setLoading(true);
    const res = await userRolePermissionService.getAll();
    const sortedData = [...res.data].sort((a, b) => a.id - b.id);
    setData(sortedData);
    setLoading(false);
  };

  const togglePermission = (id: number) => {
    if (selectedPermissionIds.includes(id)) {
      setSelectedPermissionIds(selectedPermissionIds.filter((p) => p !== id));
    } else {
      setSelectedPermissionIds([...selectedPermissionIds, id]);
    }
  };
  function formatLabel(value: string) {
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  function groupByModel(permissions: IUserRolePermission[]) {
    return permissions.reduce<Record<string, IUserRolePermission[]>>(
      (acc, perm) => {
        const model = perm.model;

        if (!acc[model]) acc[model] = [];
        acc[model].push(perm);

        return acc;
      },
      {},
    );
  }
  const onUpdatePermissions = async () => {
    if (!selectedRole) return;

    const payload = {
      role_id: selectedRole.id,
      permission_ids: selectedPermissionIds,
    };

    try {
      const response = await userRoleService.updatePermissions(payload);
      if (response.success) {
        pushMessageForToast({
          severity: "success",
          message: "Permissions updated successfully",
        });
        loadUserRoles();

        // Refresh current user's abilities if their role was updated
        if (currentUser?.role?.id === selectedRole.id) {
          await refreshCurrentUser();
        }
      } else {
        pushMessageForToast({
          severity: "error",
          message: response?.errors?.detail || response?.errors?.message,
        });
      }
    } catch (error) {
      pushMessageForToast({
        severity: "error",
        message: "An error occurred while updating permissions",
      });
    }
  };

  if (loading) {
    return <div>Loading Permissions...</div>;
  }
  return (
    <>
      <div className="flex flex-column gap-4">
        {Object.entries(groupedPermissions).map(([model, perms]) => (
          <div key={model} className="border-1 surface-border border-round p-4">
            <div className="text-lg font-semibold mb-3">
              {formatLabel(model)}
            </div>

            <div className="flex flex-column gap-2">
              {perms.map((perm) => (
                <div key={perm.id} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`perm-${perm.id}`}
                    checked={selectedPermissionIds.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                  />
                  <label
                    htmlFor={`perm-${perm.id}`}
                    className="cursor-pointer capitalize">
                    {perm.name
                      ?.replace(new RegExp(perm.model, "i"), "") // remove model name
                      ?.replace(/^Can\s/i, "") // optional: remove "Can " prefix
                      .trim()}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-content-end mt-4">
          <Button
            label="Update Permissions"
            icon="pi pi-check"
            onClick={onUpdatePermissions}
          />
        </div>
      </div>
    </>
  );
}

export default UserRolePermission;
